import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { UiPath } from '../components/UiPath';

/** What the contract says happens when the committed term runs out. */
const END_OF_TERM: { choice: string; body: string }[] = [
  {
    choice: 'Renews automatically for another term',
    body: 'The commitment rolls into its next term at the boundary. The signing date never moves — the term count goes up and the end date is re-derived from it, so “which year of this contract are we in” stays answerable from the document. One contract covering several subscriptions rolls once for the boundary they all cross, not once per subscription.',
  },
  {
    choice: 'Ends — the commitment simply runs out',
    body: 'The subscription does not renew past the boundary. Revvly also declines to invoice a period in advance once that period starts past the committed end, because the money would be collected for a term nobody has agreed to yet.',
  },
  {
    choice: 'Renews unless notice is given',
    body: 'Once the notice window opens — the committed end less the notice days — Revvly raises one item in the renewal approval queue, and the boundary is blocked until somebody decides it. A decision nobody was asked for is not a decision, which is why the notice pass and the block are two halves of the same posture. Running daily through a 60-day window raises exactly one item, not sixty.',
  },
];

/** What the contract says leaving early costs. */
const EARLY_EXIT: { choice: string; body: string }[] = [
  {
    choice: 'Charge the remainder now',
    body: 'Invoices the owed periods as a one-time termination fee. Service still ends at the current period end, and the fee never enters MRR — it is not recurring revenue.',
  },
  {
    choice: 'Keep billing to the committed end',
    body: 'The subscription stays active and keeps billing period by period until the term runs out, then ends. Every month of revenue is backed by a real invoice, which is the posture finance usually prefers.',
  },
  {
    choice: 'Waive the remainder',
    body: 'Charges nothing. The amount written off is recorded on the cancellation, so the waiver reads as a decision rather than an oversight.',
  },
];

export function ContractsGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Sell a committed term — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Contracts &amp; committed terms</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Commit a customer for a year, <em>bill them monthly</em>.
        </h1>
        <p className="dp-oneliner">
          A contract records the agreement a subscription is sold under: how long the commitment
          runs, what happens when it ends, and what leaving early costs. It governs the commitment
          boundary and nothing else — the price, the cadence and the invoices stay with the
          subscription.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          The field this whole feature exists for
        </h2>
        <p className="dp-plain">
          <strong>Term length is not billing cadence.</strong> &ldquo;Annual commitment paid
          monthly&rdquo; is a twelve-month term on a monthly subscription: twelve invoices, one
          commitment. Before contracts there was nowhere to put the twelve, so it lived in a
          spreadsheet and the renewal engine treated month thirteen exactly like month two.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Leave the term blank and the contract is <strong>evergreen</strong> — no committed span,
          which is the honest shape for an MSA. That is a normal contract, not an incomplete one: it
          records the document and its terms without publishing a boundary.
        </p>
        <UiPath
          steps={['Clients', 'open a client', 'Subscriptions', 'Contracts', 'Record contract']}
          note="Under the subscriptions rather than on Overview — a contract's whole job is to carry the committed term of the subscriptions above it."
        />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Record what you signed
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Type</div>
            <div className="dp-field-v">
              SOW, MSA, Amendment, NDA or Other. A committed term normally lives on an SOW; the MSA
              above it is usually evergreen.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Status</div>
            <div className="dp-field-v">
              Signed, Out for signature, or Draft. Record a deal that is still out for signature —
              the term only starts governing renewals once you attach it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Signed on vs. term starts</div>
            <div className="dp-field-v">
              Two dates on purpose. A contract signed in January for a March go-live is the ordinary
              case, and only the term start anchors the commitment; the signing date is the record
              of when the relationship began.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Term (months)</div>
            <div className="dp-field-v">
              How long the commitment runs. Blank means evergreen. The end date is derived from the
              term start, so it moves when either changes — and a term edit re-derives the boundary
              on every subscription the contract covers.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">At the end of the term</div>
            <div className="dp-field-v">
              Ends, renews automatically, or renews unless notice is given — with the notice days
              when that is the answer. Section 04 is what each one does.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">If they leave early</div>
            <div className="dp-field-v">
              A default for a cancellation, not the answer. Leave it silent and a cancellation
              inside a committed term has to state a policy of its own — section 05.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Attach it to a subscription
        </h2>
        <p className="dp-plain">
          Recording a contract commits nothing. Attaching it is what publishes a committed boundary
          onto a subscription — and it never touches that subscription&rsquo;s service or invoice
          dates. Rewriting those from a document date would re-bill periods that have already been
          invoiced.
        </p>
        <UiPath
          steps={[
            'Clients',
            'open a client',
            'Subscriptions',
            'Contracts',
            'the contract row',
            'Attach',
          ]}
          note="Attaching is a change to an existing subscription, so it happens here rather than in the create wizard — create the subscription, then attach it in the card underneath."
        />
        <p className="dp-plain" style={{ marginTop: 20 }}>
          One contract may cover many subscriptions. A subscription takes its committed term from
          exactly one contract, because a commitment boundary has to have a single source — detach
          the first before attaching a second. The refusals worth knowing about:
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">A term the cadence cannot express</div>
            <div className="dp-field-v">
              A 13-month commitment on a quarterly subscription has no boundary to land on — 4.33
              periods either under-bills the commitment or bills past the end of the contract, and
              there is no third answer. Pick a term the cadence divides into. Twelve months on a
              monthly plan, the case this field exists for, divides cleanly.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">A term that outlives service</div>
            <div className="dp-field-v">
              A subscription with auto-renew off ends at its current period end. A commitment
              running past that has the two records promising different things about the same
              months, so it is refused rather than reconciled.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Nothing left to govern</div>
            <div className="dp-field-v">
              A cancelled or expired subscription has no renewal boundary left, and a cancelled or
              expired contract commits nothing. Neither can be attached — so neither is offered.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">A commitment across clients</div>
            <div className="dp-field-v">
              A contract belongs to one client and can only cover that client&rsquo;s subscriptions.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          What happens at the committed end
        </h2>
        <p className="dp-section-sub">
          The contract governs the commitment boundary only. Ordinary period boundaries inside the
          term stay with the subscription&rsquo;s own auto-renew setting — and a contract narrows a
          renewal rather than forcing one: it can stop or gate a renewal, never compel one the
          subscription would not have made.
        </p>
        <ol className="qs-steps">
          {END_OF_TERM.map((e, i) => (
            <li className="qs-step" key={e.choice}>
              <span className="qs-phase-num" style={{ marginRight: 12 }}>
                {i + 1}
              </span>
              <div>
                <div className="qs-step-label">{e.choice}</div>
                <div className="qs-step-detail">{e.body}</div>
              </div>
            </li>
          ))}
        </ol>
        <UiPath
          steps={['Renewals', 'the approval queue']}
          note="Where a notice-period decision lands. It is the same queue an operator already works from, so a committed term does not add a surface."
        />
        <div className="api-note api-note-warn" style={{ marginTop: 22 }}>
          <span className="api-note-icon" aria-hidden="true">
            !
          </span>
          <div>
            <strong>Set the term start on a period boundary where you can.</strong> The boundary
            Revvly acts on is the first period end at or after the committed end — a term ending on
            the 15th under a subscription anchored to the 1st is reached at the following month end.
            For an auto-renewing term that is immaterial. For &ldquo;Ends&rdquo; and &ldquo;Renews
            unless notice is given&rdquo; it means one extra period is billed before the
            contract&rsquo;s own terms are consulted. Ending short of a committed term and billing a
            period past one are both wrong in ways only you can choose between, so Revvly does
            neither silently: it logs the misalignment where it happens.
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          What leaving early costs
        </h2>
        <p className="dp-plain">
          Ending a subscription with uninvoiced periods still inside a committed term asks for a
          policy, and refuses to proceed without one. The dialog states the exposure first — how
          many periods remain, when the commitment ends, and what is owed — so the choice is made
          against a figure rather than a feeling.
        </p>
        <UiPath
          steps={['Subscriptions', 'open a subscription', 'End subscription']}
          note="The policy recorded on the contract is the default. Whoever cancels can override it — the contract is what was agreed, the cancellation is what happened."
        />
        <ol className="qs-steps" style={{ marginTop: 20 }}>
          {EARLY_EXIT.map((e, i) => (
            <li className="qs-step" key={e.choice}>
              <span className="qs-phase-num" style={{ marginRight: 12 }}>
                {i + 1}
              </span>
              <div>
                <div className="qs-step-label">{e.choice}</div>
                <div className="qs-step-detail">{e.body}</div>
              </div>
            </li>
          ))}
        </ol>
        <p className="dp-plain" style={{ marginTop: 20 }}>
          A prepaid span is the mirror image: the money is already in, so the question is whether
          any of it goes back. That is the commitment posture on the schedule — binding, refundable,
          or neither — and a refundable one issues a{' '}
          <Link to="/docs/guides/credit-notes">credit note</Link> for the whole periods the customer
          paid for and will not consume, components paid ahead included. See{' '}
          <Link to="/docs/guides/billing-schedules">billing schedules</Link>.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          Changing or retiring a contract
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Editing the term</div>
            <div className="dp-field-v">
              Re-derives the boundary on every attached subscription in the same write, so the two
              copies cannot disagree. The same fit checks apply, so a term edit cannot be used to
              reach a state an attach would have refused. Clearing the term makes the contract
              evergreen and lifts the boundary everywhere.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Detaching</div>
            <div className="dp-field-v">
              Clears the link and the committed boundary together. The subscription goes back to
              renewing on its own auto-renew setting.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Marking it cancelled or expired</div>
            <div className="dp-field-v">
              Refused while it still covers subscriptions — their boundaries would go on gating
              renewals against a document that is over. Detach them first; quietly lifting
              commitments off several subscriptions as a side effect of a status edit is not
              something anyone asked for.
            </div>
          </div>
        </div>
        <ul className="dp-connects" style={{ marginTop: 20 }}>
          <li>
            <Link to="/docs/guides/renewals" style={{ color: 'inherit', textDecoration: 'none' }}>
              Run the renewal cycle ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/billing-schedules"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Prepaid terms &amp; deferred go-live ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/credit-notes"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Credit notes ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/concepts/subscription"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Subscription concept ›
            </Link>
          </li>
        </ul>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          Sub-processor and breach terms
        </h2>
        <p className="dp-plain">
          A contract can also carry two operational terms, so that &ldquo;which clients do I tell,
          and how soon&rdquo; is a lookup rather than a reading of PDFs. For{' '}
          <strong>sub-processor changes</strong>, record what the client is owed before a new
          sub-processor starts: to be told, to be told and allowed to object within some days, or to
          agree first, with the days of notice and how notice is given. For{' '}
          <strong>breach notification</strong>, record the hours after you become aware, or leave
          the hours blank when the contract gives no number.
        </p>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Type them in</div>
            <div className="dp-field-v">
              On a client&rsquo;s contracts card, Add terms (or Edit terms) opens the two terms for
              that contract. A term the contract does not state stays blank, which is different from
              a contract nobody has checked yet.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Read them from a PDF</div>
            <div className="dp-field-v">
              Mark a PDF as Contract on the client&rsquo;s Documents tab, including a file from
              their Google Drive folder, and choose Read terms. Revvly suggests the terms and shows
              what it read. Nothing is saved until you check them against the contract and press
              Save terms, which records the contract, or updates the one already made from that
              file. A value it was unsure of is left blank rather than guessed. Needs the CRM
              add-on.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">See them across clients</div>
            <div className="dp-field-v">
              Contracts, then Clauses across clients, lists every client bound by a signed contract
              with its terms and its DPO contacts. Filter by sub-processor changes or breach
              notification. A client with no terms recorded reads Not checked, and one whose
              contract is silent reads Not stated. Label a contact <strong>dpo</strong> to have them
              listed as the person to tell.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
